import { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../api';
import {
  LIBRARIAN_FAMILIES,
  canonicalIssueType,
  familyLabel,
  humanize,
  legacyNumericTargetForRow,
  normalizeValue,
  safeBulkActionForRow,
  suggestedCanonicalForRow,
} from './taxonomyLibrarianUtils';


export default function useTaxonomyReviewActions({ setJob }) {
  const [cleanupNotice, setCleanupNotice] = useState('');
  const [cleanupError, setCleanupError] = useState('');
  const [cleaningQueueId, setCleaningQueueId] = useState('');
  const [reviewingQueueId, setReviewingQueueId] = useState('');
  const [bulkApplying, setBulkApplying] = useState(false);
  const [applyingServicesSuggestions, setApplyingServicesSuggestions] = useState(false);
  const [normalizingLegacy, setNormalizingLegacy] = useState(false);
  const [terms, setTerms] = useState([]);
  const [facets, setFacets] = useState([]);
  const [servicesSplitPass, setServicesSplitPass] = useState({
    open: false,
    row: null,
    loading: false,
    error: '',
    candidates: [],
    count: 0,
    already_classified_count: 0,
    total_matching_count: 0,
    has_more: false,
  });

  const loadTerms = useCallback(async () => {
    try {
      const [loadedTerms, loadedFacets] = await Promise.all([
        Promise.all(LIBRARIAN_FAMILIES.map(family => api.getVocabularyTerms(family, null, { includeUsage: true }))),
        Promise.all(LIBRARIAN_FAMILIES.map(family => api.getVocabularyFacets(family, null, { includeUsage: true }))),
      ]);
      setTerms(loadedTerms.flat());
      setFacets(loadedFacets.flat());
    } catch (_err) {
      setTerms([]);
      setFacets([]);
    }
  }, []);

  useEffect(() => { loadTerms(); }, [loadTerms]);

  const termKey = term => `${normalizeValue(term.family || 'use_case_tag') || 'use_case_tag'}:${normalizeValue(term.value)}`;
  const rowTermKey = row => `${normalizeValue(row.family || 'use_case_tag') || 'use_case_tag'}:${normalizeValue(row.tag)}`;
  const termsByValue = useMemo(() => new Map(terms.map(term => [termKey(term), term])), [terms]);
  const termsById = useMemo(() => new Map(terms.map(term => [String(term.id), term])), [terms]);
  const approvedTermsForRow = useCallback(row => {
    const family = normalizeValue(row.family || 'use_case_tag') || 'use_case_tag';
    const source = normalizeValue(row.tag || row.raw_value);
    return terms
      .filter(term => normalizeValue(term.family || 'use_case_tag') === family && term.status === 'approved' && normalizeValue(term.value) !== source)
      .sort((a, b) => String(a.label || a.value).localeCompare(String(b.label || b.value)));
  }, [terms]);
  const approvedFacetsForRow = useCallback(row => {
    const family = normalizeValue(row.family || 'use_case_tag') || 'use_case_tag';
    return facets
      .filter(facet => normalizeValue(facet.family || 'use_case_tag') === family && facet.status === 'approved')
      .sort((a, b) => String(a.label || a.value).localeCompare(String(b.label || b.value)));
  }, [facets]);

  const findTermForRow = useCallback(row => {
    if (row.source_table === 'vocabulary_terms' && row.source_id) {
      const byId = termsById.get(String(row.source_id));
      if (byId) return byId;
    }
    return termsByValue.get(rowTermKey(row)) || null;
  }, [termsById, termsByValue]);

  const removeReviewRows = useCallback(predicate => {
    setJob(prev => {
      if (!prev?.result?.review_queue) return prev;
      const nextQueue = prev.result.review_queue.filter(item => !predicate(item));
      return {
        ...prev,
        result: {
          ...prev.result,
          review_queue: nextQueue,
          summary: {
            ...(prev.result.summary || {}),
            review_rows: nextQueue.length,
          },
        },
      };
    });
  }, [setJob]);

  const updateTermInState = useCallback(term => {
    setTerms(prev => [...prev.filter(item => item.id !== term.id), term]);
  }, []);

  const runReviewMutation = useCallback(async (row, mutation) => {
    setReviewingQueueId(row.queue_id);
    setCleanupError('');
    setCleanupNotice('');
    try {
      return await mutation();
    } catch (err) {
      setCleanupError(err.message || String(err));
      return null;
    } finally {
      setReviewingQueueId('');
    }
  }, []);

  const approveTerm = row => runReviewMutation(row, async () => {
    const family = normalizeValue(row.family || 'use_case_tag') || 'use_case_tag';
    const term = findTermForRow(row);
    const updated = term
      ? await api.updateVocabularyTerm(term.id, { status: 'approved' })
      : await api.createVocabularyTerm(family, humanize(row.tag), { status: 'approved' });
    updateTermInState(updated);
    removeReviewRows(item => normalizeValue(item.tag) === normalizeValue(row.tag) && ['pending_term_suggestion', 'used_not_in_approved_vocabulary'].includes(item.issue_type));
    setCleanupNotice(`Approved ${row.tag}. Run the Librarian again to verify.`);
  });

  const retireTerm = row => runReviewMutation(row, async () => {
    const family = normalizeValue(row.family || 'use_case_tag') || 'use_case_tag';
    if (row.source_table === 'use_case_tags' && row.source_id) {
      await api.updateTag(row.source_id, { active: false });
      await loadTerms();
    } else {
      const term = findTermForRow(row);
      if (!term) {
        throw new Error(`Could not find a vocabulary term for ${row.tag}. Open Taxonomy to inspect it.`);
      }
      const updated = await api.updateVocabularyTerm(term.id, { status: 'retired' });
      updateTermInState(updated);
    }
    removeReviewRows(item => (
      normalizeValue(item.tag) === normalizeValue(row.tag)
      && (normalizeValue(item.family || 'use_case_tag') || 'use_case_tag') === family
      && [
        'pending_term_suggestion',
        'approved_term_unused',
        'taxonomy_value_leak',
        'card_tag_structured_duplicate',
        'non_normalized_term',
        'retired_term_still_used',
      ].includes(canonicalIssueType(item.issue_type))
    ));
    setCleanupNotice(`Retired ${row.tag}. Run the Librarian again to verify.`);
    return true;
  });

  const setFacetForTerm = async (row, selectedFacet = null) => {
    const term = findTermForRow(row);
    if (!term) {
      setCleanupError(`Could not find a vocabulary term for ${row.tag}. Open Taxonomy to inspect it.`);
      return;
    }
    if (selectedFacet == null) {
      const proposedFacet = window.prompt(`Propose new facet for ${familyLabel(row.family)}`, '');
      if (proposedFacet == null) return;
      const normalizedFacet = normalizeValue(proposedFacet);
      if (!normalizedFacet) {
        setCleanupError('Facet is required.');
        return;
      }
      return runReviewMutation(row, async () => {
        const family = normalizeValue(row.family || 'use_case_tag') || 'use_case_tag';
        const facet = await api.createVocabularyFacet(family, proposedFacet, {
          raw_json: {
            source: 'taxonomy_librarian',
            term: row.tag,
            queue_id: row.queue_id,
            issue_type: row.issue_type,
          },
        });
        setFacets(prev => [...prev.filter(item => item.id !== facet.id), facet]);
        setCleanupNotice(`Proposed ${normalizedFacet} for ${familyLabel(row.family)}. Approve the facet in Taxonomy before assigning it.`);
      });
    }
    const normalizedFacet = normalizeValue(selectedFacet);
    return runReviewMutation(row, async () => {
      const updated = await api.updateVocabularyTerm(term.id, { facet: normalizedFacet });
      updateTermInState(updated);
      removeReviewRows(item => item.queue_id === row.queue_id);
      setCleanupNotice(`Set ${row.tag} facet to ${normalizedFacet}. Run the Librarian again to verify.`);
    });
  };

  const cleanTermLabel = async (row, options = {}) => {
    const term = findTermForRow(row);
    if (!term) {
      setCleanupError(`Could not find a vocabulary term for ${row.tag}. Open Taxonomy to inspect it.`);
      return;
    }
    const cleaned = String(term.label || row.raw_value || row.tag)
      .replace(/#/g, '')
      .replace(/\s+/g, ' ')
      .trim();
    const label = options.prompt === false
      ? (cleaned || humanize(row.tag))
      : window.prompt(`Clean label for ${row.tag}`, cleaned || humanize(row.tag));
    if (label == null) return null;
    const nextLabel = label.trim();
    if (!nextLabel) {
      setCleanupError('Label is required.');
      return;
    }
    return runReviewMutation(row, async () => {
      const updated = await api.updateVocabularyTerm(term.id, { label: nextLabel });
      updateTermInState(updated);
      removeReviewRows(item => item.queue_id === row.queue_id);
      setCleanupNotice(`Updated ${row.tag} label. Run the Librarian again to verify.`);
      return updated;
    });
  };

  const bulkApplySafeRows = async rows => {
    const eligibleRows = (rows || []).filter(row => safeBulkActionForRow(row));
    if (!eligibleRows.length) {
      setCleanupNotice('No safe bulk-applicable rows in the current view.');
      return;
    }
    const confirmed = window.confirm(
      `Apply ${eligibleRows.length} safe Librarian recommendation${eligibleRows.length === 1 ? '' : 's'}? This will only run deterministic cleanup actions.`
    );
    if (!confirmed) return;
    setBulkApplying(true);
    setCleanupError('');
    setCleanupNotice('');
    let applied = 0;
    try {
      for (const row of eligibleRows) {
        const bulkAction = safeBulkActionForRow(row);
        if (bulkAction === 'apply_legacy_target') {
          const target = legacyNumericTargetForRow(row);
          if (!target) continue;
          const result = await applyTaxonomyRewrite(row, target, { sourceValue: sourceValueForRow(row) });
          if (result) applied += 1;
        } else if (bulkAction === 'normalize_value') {
          const result = await applyTaxonomyRewrite(row, row.tag, { sourceValue: sourceValueForRow(row), allowSameNormalized: true });
          if (result) applied += 1;
        } else if (bulkAction === 'clean_label') {
          const result = await cleanTermLabel(row, { prompt: false });
          if (result) applied += 1;
        } else if (bulkAction === 'retire_boundary_duplicate') {
          const result = await retireTerm(row);
          if (result) applied += 1;
        }
      }
      const failed = eligibleRows.length - applied;
      setCleanupNotice(
        `Applied ${applied} safe Librarian recommendation${applied === 1 ? '' : 's'}${failed ? `; ${failed} did not apply and still need review` : ''}. Run the Librarian again to verify.`
      );
    } finally {
      setBulkApplying(false);
    }
  };

  const sourceValueForRow = useCallback(row => {
    const term = findTermForRow(row);
    if (term?.value) return term.value;
    if (row.raw_value && !String(row.raw_value).includes('|')) return row.raw_value;
    return row.tag;
  }, [findTermForRow]);

  const ignoreReviewRow = async row => {
    const note = window.prompt(
      `Ignore ${row.tag}? Optional note for the Librarian/admin history:`,
      ''
    );
    if (note == null) return;
    return runReviewMutation(row, async () => {
      await api.ignoreTaxonomyLibrarianProposal({
        review_type: row.review_type || 'issue',
        family: normalizeValue(row.family || 'use_case_tag') || 'use_case_tag',
        tag: normalizeValue(row.tag || ''),
        canonical_tag: normalizeValue(suggestedCanonicalForRow(row) || ''),
        candidate_tag: normalizeValue(row.candidate_tag || ''),
        issue_type: normalizeValue(row.issue_type || ''),
        issue_subtype: normalizeValue(row.issue_subtype || ''),
        recommended_action: normalizeValue(row.recommended_action || ''),
        reason: note.trim() || `Ignored from Taxonomy Librarian: ${row.reason || row.recommended_action || ''}`,
      });
      removeReviewRows(item => (
        item.queue_id === row.queue_id
        || (
          (normalizeValue(item.family || 'use_case_tag') || 'use_case_tag') === (normalizeValue(row.family || 'use_case_tag') || 'use_case_tag')
          && normalizeValue(item.tag) === normalizeValue(row.tag)
          && canonicalIssueType(item.issue_type) === canonicalIssueType(row.issue_type)
          && (suggestedCanonicalForRow(item) || '') === (suggestedCanonicalForRow(row) || '')
        )
      ));
      setCleanupNotice(`Ignored ${row.tag}. It will be suppressed in future Librarian runs.`);
    });
  };

  const normalizeLegacyBuckets = async () => {
    setNormalizingLegacy(true);
    setCleanupError('');
    setCleanupNotice('');
    try {
      const result = await api.normalizeLegacyTaxonomyBuckets('Taxonomy Librarian: normalize legacy numbered buckets');
      await loadTerms();
      removeReviewRows(item => Boolean(legacyNumericTargetForRow(item)));
      const affected = Number(result?.affected_rows || 0);
      setCleanupNotice(`Normalized legacy numbered buckets; ${affected.toLocaleString()} current row${affected === 1 ? '' : 's'} updated. Run the Librarian again to verify.`);
    } catch (err) {
      setCleanupError(err.message || String(err));
    } finally {
      setNormalizingLegacy(false);
    }
  };

  const applyTaxonomyRewrite = async (row, replacement, options = {}) => {
    const family = normalizeValue(row.family || 'use_case_tag') || 'use_case_tag';
    const sourceValue = normalizeValue(options.sourceValue || sourceValueForRow(row));
    const normalized = normalizeValue(replacement);
    const sourceTerm = findTermForRow(row);
    const sourceLabel = sourceValue || row.tag;
    if (!normalized) {
      setCleanupError('Replacement term is required.');
      return;
    }
    if (!sourceValue) {
      setCleanupError(`Could not determine the source value for ${row.tag}. Open Taxonomy to inspect it.`);
      return;
    }
    if (normalized === sourceValue && !options.allowSameNormalized) {
      setCleanupError('Replacement term must be different from the current term.');
      return;
    }
    return runReviewMutation(row, async () => {
      const result = await api.rewriteTaxonomyValue({
        family,
        source_value: sourceValue,
        target_value: normalized,
        source_term_id: sourceTerm?.id || null,
        target_label: options.targetLabel || humanize(normalized),
        facet: options.facet === undefined ? (family === 'use_case_tag' ? 'activity' : null) : options.facet,
        reason: options.reason || `Taxonomy Librarian rewrite: ${sourceLabel} -> ${normalized}`,
        allow_same_normalized: Boolean(options.allowSameNormalized || normalized === sourceValue),
      });
      await loadTerms();
      removeReviewRows(item => (
        (normalizeValue(item.family || 'use_case_tag') || 'use_case_tag') === family
        && (
          item.queue_id === row.queue_id
          || normalizeValue(item.tag) === normalizeValue(row.tag)
          || normalizeValue(item.raw_value) === sourceValue
          || normalizeValue(item.candidate_tag) === normalizeValue(row.tag)
        )
      ));
      setCleanupNotice(`Rewrote ${sourceLabel} to ${result.target?.value || normalized}; ${result.affected_rows || 0} current row${result.affected_rows === 1 ? '' : 's'} updated.`);
      return result;
    });
  };

  const applyLocationRewrite = applyTaxonomyRewrite;

  const rewriteLocationLeak = async row => {
    const replacement = window.prompt(
      `Rewrite ${row.tag} to universal ${familyLabel(row.family)} value`,
      row.canonical_tag || ''
    );
    if (replacement == null) return;
    await applyTaxonomyRewrite(row, replacement);
  };

  const rewriteTaxonomyRow = async (row, defaultTarget = '') => {
    const sourceValue = sourceValueForRow(row);
    const replacement = window.prompt(
      `Rewrite ${sourceValue || row.tag} to approved ${familyLabel(row.family)} value`,
      defaultTarget || row.canonical_tag || row.tag || ''
    );
    if (replacement == null) return;
    await applyTaxonomyRewrite(row, replacement, { sourceValue });
  };

  const mergeIntoExistingTerm = async (row, targetValue) => {
    if (!targetValue) return;
    const family = normalizeValue(row.family || 'use_case_tag') || 'use_case_tag';
    const normalizedTarget = normalizeValue(targetValue);
    const target = termsByValue.get(`${family}:${normalizedTarget}`);
    await applyTaxonomyRewrite(row, normalizedTarget, {
      sourceValue: sourceValueForRow(row),
      targetLabel: target?.label || humanize(normalizedTarget),
      facet: target?.facet || null,
      reason: `Taxonomy Librarian merge into existing term: ${row.tag} -> ${normalizedTarget}`,
    });
  };

  const mergeAlias = async row => {
    const family = normalizeValue(row.family || 'use_case_tag') || 'use_case_tag';
    const sourceValue = normalizeValue(row.candidate_tag);
    const targetValue = normalizeValue(row.canonical_tag);
    const source = termsByValue.get(`${family}:${sourceValue}`);
    const target = termsByValue.get(`${family}:${targetValue}`);
    if (!source || !target) {
      setCleanupError('Both alias terms must exist in Taxonomy before they can be merged.');
      return;
    }
    return runReviewMutation(row, async () => {
      const result = await api.mergeVocabularyTerm(source.id, target.id, `Taxonomy Librarian merge: ${row.candidate_tag} -> ${row.canonical_tag}`);
      await loadTerms();
      removeReviewRows(item => (
        (item.review_type === 'alias_candidate' && normalizeValue(item.candidate_tag) === sourceValue && normalizeValue(item.canonical_tag) === targetValue)
        || normalizeValue(item.tag) === sourceValue
      ));
      setCleanupNotice(`Merged ${row.candidate_tag} into ${row.canonical_tag}; ${result.affected_rows || 0} current row${result.affected_rows === 1 ? '' : 's'} updated.`);
    });
  };

  const rejectAlias = row => runReviewMutation(row, async () => {
    await api.rejectCardTagCleanupAlias({
      canonical_tag: row.canonical_tag,
      candidate_tag: row.candidate_tag,
      reason: `Rejected from Taxonomy Librarian: ${row.reason || row.recommended_action || ''}`,
    });
    removeReviewRows(item => item.review_type === 'alias_candidate' && item.candidate_tag === row.candidate_tag && item.canonical_tag === row.canonical_tag);
    setCleanupNotice(`Rejected alias ${row.candidate_tag} -> ${row.canonical_tag}. It will be suppressed in future reports.`);
  });

  const startServicesSplitPass = async row => {
    setCleanupError('');
    setCleanupNotice('');
    const family = normalizeValue(row.family || 'category') || 'category';
    const sourceValue = normalizeValue(sourceValueForRow(row));
    setServicesSplitPass({
      open: true,
      row,
      loading: true,
      error: '',
      candidates: [],
      count: 0,
      already_classified_count: 0,
      total_matching_count: 0,
      has_more: false,
    });
    try {
      const result = await api.getTaxonomyServicesSplitCandidates({
        family,
        source_value: sourceValue,
        limit: '200',
      });
      setServicesSplitPass({
        open: true,
        row,
        loading: false,
        error: '',
        candidates: result.candidates || [],
        count: result.count || 0,
        already_classified_count: result.already_classified_count || 0,
        total_matching_count: result.total_matching_count || result.count || 0,
        has_more: Boolean(result.has_more),
      });
    } catch (err) {
      setServicesSplitPass(prev => ({
        ...prev,
        loading: false,
        error: err.message || String(err),
      }));
    }
  };

  const closeServicesSplitPass = () => {
    setServicesSplitPass({
      open: false,
      row: null,
      loading: false,
      error: '',
      candidates: [],
      count: 0,
      already_classified_count: 0,
      total_matching_count: 0,
      has_more: false,
    });
  };

  const classifyServicesSplitCandidate = (candidate, targetLane) => {
    const row = servicesSplitPass.row;
    if (!row || !candidate?.place_master_id || !candidate?.card_key) return null;
    const family = normalizeValue(row.family || 'category') || 'category';
    const sourceValue = normalizeValue(sourceValueForRow(row));
    return runReviewMutation(row, async () => {
      await api.classifyTaxonomyServicesSplitCard({
        family,
        source_value: sourceValue,
        place_master_id: candidate.place_master_id,
        card_key: candidate.card_key,
        target_lane: targetLane,
        reason: `Taxonomy Librarian services split: ${sourceValue} -> ${targetLane}`,
      });
      setServicesSplitPass(prev => ({
        ...prev,
        candidates: prev.candidates.filter(item => !(
          item.place_master_id === candidate.place_master_id && item.card_key === candidate.card_key
        )),
        count: Math.max(Number(prev.count || 0) - 1, 0),
        already_classified_count: Number(prev.already_classified_count || 0) + 1,
      }));
      setCleanupNotice(`Classified ${candidate.place_name || candidate.place_master_id} / ${candidate.card_key} as ${targetLane}. Republish if it should surface live.`);
    });
  };

  const applyVisibleServicesSplitSuggestions = async () => {
    const row = servicesSplitPass.row;
    if (!row) return;
    const family = normalizeValue(row.family || 'category') || 'category';
    const sourceValue = normalizeValue(sourceValueForRow(row));
    const suggested = (servicesSplitPass.candidates || []).filter(candidate => (
      normalizeValue(candidate.suggested_target_lane)
      && normalizeValue(candidate.current_retrieval_lane) !== normalizeValue(candidate.suggested_target_lane)
    ));
    if (!suggested.length) {
      setCleanupNotice('No visible services suggestions to apply.');
      return;
    }
    const confirmed = window.confirm(
      `Apply ${suggested.length} visible services lane suggestion${suggested.length === 1 ? '' : 's'} for ${sourceValue}? Review ambiguous cards manually.`
    );
    if (!confirmed) return;
    setApplyingServicesSuggestions(true);
    setReviewingQueueId(row.queue_id);
    setCleanupError('');
    setCleanupNotice('');
    let applied = 0;
    const updatedByKey = new Map();
    try {
      for (const candidate of suggested) {
        try {
          const result = await api.classifyTaxonomyServicesSplitCard({
            family,
            source_value: sourceValue,
            place_master_id: candidate.place_master_id,
            card_key: candidate.card_key,
            target_lane: candidate.suggested_target_lane,
            reason: `Taxonomy Librarian suggested services split: ${sourceValue} -> ${candidate.suggested_target_lane}`,
          });
          const nextCandidate = result.candidate || candidate;
          updatedByKey.set(`${candidate.place_master_id}:${candidate.card_key}`, nextCandidate);
          applied += 1;
        } catch (err) {
          setCleanupError(err.message || String(err));
          break;
        }
      }
      if (updatedByKey.size) {
        setServicesSplitPass(prev => ({
          ...prev,
          candidates: prev.candidates.filter(candidate => !updatedByKey.has(`${candidate.place_master_id}:${candidate.card_key}`)),
          count: Math.max(Number(prev.count || 0) - updatedByKey.size, 0),
          already_classified_count: Number(prev.already_classified_count || 0) + updatedByKey.size,
        }));
      }
      const failed = suggested.length - applied;
      setCleanupNotice(
        `Applied ${applied} services lane suggestion${applied === 1 ? '' : 's'}${failed ? `; ${failed} still need review` : ''}. Republish cards that should surface live.`
      );
    } finally {
      setApplyingServicesSuggestions(false);
      setReviewingQueueId('');
    }
  };

  const migrateToRetrievalLane = row => runReviewMutation(row, async () => {
    const family = normalizeValue(row.family || 'use_case_tag') || 'use_case_tag';
    const sourceValue = normalizeValue(sourceValueForRow(row));
    const result = await api.migrateTaxonomyValueToRetrievalLane({
      family,
      source_value: sourceValue,
      reason: `Taxonomy Librarian retrieval-lane migration: ${sourceValue}`,
    });
    await loadTerms();
    removeReviewRows(item => (
      ['utility_kind', 'practical_use'].includes(normalizeValue(item.family || ''))
      && normalizeValue(item.tag || item.raw_value) === sourceValue
    ));
    setCleanupNotice(`Moved ${sourceValue} to ${result.target_lane || 'retrieval lane'}; ${result.affected_rows || 0} row${result.affected_rows === 1 ? '' : 's'} updated. Run the Librarian again to verify.`);
  });

  const removeCurrentRows = async row => {
    setCleaningQueueId(row.queue_id);
    setCleanupError('');
    setCleanupNotice('');
    try {
      const result = await api.removeCardTagCleanupTag({
        tag: row.tag,
        place_master_id: row.place_master_id,
        card_key: row.card_key || null,
        source_table: row.source_table || null,
        reason: `taxonomy librarian: ${row.recommended_action}`,
      });
      const currentSources = new Set(['places.guest_use_case_tags', 'place_cards.tags', 'published_retrieval_items.tags']);
      setJob(prev => {
        if (!prev?.result?.review_queue) return prev;
        const nextQueue = prev.result.review_queue.filter(item => {
          const itemSources = String(item.source_table || '').split('|').map(value => value.trim()).filter(Boolean);
          if (!itemSources.some(source => currentSources.has(source))) return true;
          if (item.tag !== row.tag) return true;
          if (item.place_master_id !== row.place_master_id) return true;
          if (row.card_key && item.card_key && item.card_key !== row.card_key) return true;
          return false;
        });
        return {
          ...prev,
          result: {
            ...prev.result,
            review_queue: nextQueue,
            summary: {
              ...(prev.result.summary || {}),
              review_rows: nextQueue.length,
            },
          },
        };
      });
      setCleanupNotice(`Removed ${row.tag} from ${result.affected_rows} current row${result.affected_rows === 1 ? '' : 's'}. Run the Librarian again to verify.`);
    } catch (err) {
      setCleanupError(err.message);
    } finally {
      setCleaningQueueId('');
    }
  };

  return {
    cleanupNotice,
    cleanupError,
    setCleanupError,
    cleaningQueueId,
    reviewingQueueId,
    bulkApplying,
    applyingServicesSuggestions,
    normalizingLegacy,
    approvedTermsForRow,
    approvedFacetsForRow,
    sourceValueForRow,
    approveTerm,
    retireTerm,
    setFacetForTerm,
    cleanTermLabel,
    bulkApplySafeRows,
    ignoreReviewRow,
    normalizeLegacyBuckets,
    applyTaxonomyRewrite,
    applyLocationRewrite,
    rewriteLocationLeak,
    rewriteTaxonomyRow,
    mergeIntoExistingTerm,
    mergeAlias,
    rejectAlias,
    startServicesSplitPass,
    servicesSplitPass,
    closeServicesSplitPass,
    classifyServicesSplitCandidate,
    applyVisibleServicesSplitSuggestions,
    migrateToRetrievalLane,
    removeCurrentRows,
  };
}
